import React from "react";

import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { Application } from "src/domains/application/types";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

import { AddServiceDialog } from "./AddServiceDialog";
import { ENV_LABELS } from "./editable-services-constants";
import { EnvironmentTabs } from "./EnvironmentTabs";
import { ServiceRow } from "./ServiceRow";
import { useEditableServices } from "./useEditableServices";

// ── Main component ──────────────────────────────────────────────────────────

interface EditableServicesSectionProps {
  system: Application;
}

export function EditableServicesSection({
  system,
}: EditableServicesSectionProps): React.ReactElement {
  const {
    services,
    activeEnv,
    setActiveEnv,
    loading,
    saving,
    error,
    dirty,
    addingService,
    setAddingService,
    isEditable,
    availableServices,
    envsWithOverrides,
    handleEnvServiceChange,
    handleRemoveService,
    handleAddService,
    handleSave,
    handleDiscard,
  } = useEditableServices(system);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error && services.length === 0) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (services.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No services configured for this stack.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Services
        </Typography>
        <Chip label={services.length} size="small" variant="outlined" />
      </Stack>

      {!isEditable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Service configuration can only be edited when the stack is in draft or
          failed state.
        </Alert>
      )}

      <EnvironmentTabs
        activeEnv={activeEnv}
        envsWithOverrides={envsWithOverrides}
        onChange={setActiveEnv}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 2, display: "block" }}
      >
        {activeEnv === "dev"
          ? "Base resource allocation. Other environments inherit these values unless overridden."
          : `Overrides for ${ENV_LABELS[activeEnv]}. Values inherit from dev unless changed.`}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {services.map((service, index) => (
          <ServiceRow
            key={service.name}
            service={service}
            activeEnv={activeEnv}
            editable={isEditable}
            isBaseEnv={activeEnv === "dev"}
            onRemove={() => handleRemoveService(index)}
            onChange={(env, updates) =>
              handleEnvServiceChange(index, env, updates)
            }
          />
        ))}

        <AddServiceDialog
          isEditable={isEditable}
          availableServices={availableServices}
          addingService={addingService}
          onAdd={handleAddService}
          onCancel={() => setAddingService(false)}
          onStartAdding={() => setAddingService(true)}
        />
      </Stack>

      {isEditable && dirty && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDiscard}
            disabled={saving}
          >
            Discard
          </Button>
        </Stack>
      )}

      {error && dirty && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default EditableServicesSection;
